'use client';
import React, { useState } from 'react';
import { X, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { COMPARE_SCENARIOS, type CompareScenario } from '@/lib/archsim-data';

interface ComparePanelProps {
  onClose: () => void;
}

type MetricKey = 'rps' | 'p99' | 'errorRate' | 'availability' | 'cacheHit' | 'dbLoad';

const METRIC_LABELS: Record<MetricKey, { label: string; unit: string; higherIsBetter: boolean }> = {
  rps:          { label: 'Throughput (RPS)', unit: 'req/s', higherIsBetter: true },
  p99:          { label: 'P99 Latency', unit: 'ms', higherIsBetter: false },
  errorRate:    { label: 'Error Rate', unit: '%', higherIsBetter: false },
  availability: { label: 'Availability', unit: '%', higherIsBetter: true },
  cacheHit:     { label: 'Cache Hit Rate', unit: '%', higherIsBetter: true },
  dbLoad:       { label: 'DB Load', unit: '%', higherIsBetter: false },
};

function formatVal(key: MetricKey, v: number): string {
  if (key === 'rps') return v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`;
  if (key === 'availability') return `${v.toFixed(2)}%`;
  return `${v}`;
}

function getDelta(key: MetricKey, left: number, right: number): { pct: number; better: boolean } {
  const pct = Math.round(((right - left) / Math.max(left, 0.001)) * 100);
  const { higherIsBetter } = METRIC_LABELS[key];
  return { pct, better: higherIsBetter ? pct > 0 : pct < 0 };
}

export default function ComparePanel({ onClose }: ComparePanelProps) {
  const [activeScenario, setActiveScenario] = useState<CompareScenario>(COMPARE_SCENARIOS[0]);

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-card border border-border rounded-xl w-full max-w-3xl overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-base font-semibold text-foreground">Architecture Compare</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Side-by-side performance comparison of common architectural trade-offs</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-all">
            <X size={16} />
          </button>
        </div>

        {/* Scenario selector */}
        <div className="flex gap-2 px-6 py-3 border-b border-border overflow-x-auto scrollbar-thin">
          {COMPARE_SCENARIOS.map(s => (
            <button
              key={`compare-${s.id}`}
              onClick={() => setActiveScenario(s)}
              className={`shrink-0 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                activeScenario.id === s.id
                  ? 'bg-primary/10 text-primary border border-primary/30' :'bg-secondary text-muted-foreground border border-border hover:text-foreground'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Comparison */}
        <div className="p-6">
          {/* Column headers */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="flex flex-col items-center p-3 rounded-lg bg-secondary border border-border">
              <span className="text-sm font-semibold text-foreground">{activeScenario.left.label}</span>
              <span className="text-xs text-muted-foreground mt-0.5">Baseline</span>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">vs</span>
            </div>
            <div className="flex flex-col items-center p-3 rounded-lg bg-primary/5 border border-primary/20">
              <span className="text-sm font-semibold text-primary">{activeScenario.right.label}</span>
              <span className="text-xs text-muted-foreground mt-0.5">Improved</span>
            </div>
          </div>

          {/* Metrics comparison */}
          <div className="space-y-3">
            {(Object.keys(METRIC_LABELS) as MetricKey[]).map(key => {
              const leftVal = activeScenario.left[key];
              const rightVal = activeScenario.right[key];
              const { label, unit } = METRIC_LABELS[key];
              const { pct, better } = getDelta(key, leftVal, rightVal);
              const maxVal = Math.max(leftVal, rightVal);

              return (
                <div key={`compare-metric-${key}`} className="grid grid-cols-3 gap-4 items-center">
                  {/* Left */}
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{label}</span>
                      <span className="text-sm font-bold font-mono-nums text-foreground">
                        {formatVal(key, leftVal)}<span className="text-xs text-muted-foreground ml-0.5">{unit}</span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full bg-muted-foreground/50 rounded-full"
                        style={{ width: `${(leftVal / maxVal) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Delta */}
                  <div className="flex flex-col items-center gap-0.5">
                    <div className={`flex items-center gap-1 ${better ? 'text-positive' : pct === 0 ? 'text-muted-foreground' : 'text-danger'}`}>
                      {better ? <TrendingUp size={12} /> : pct === 0 ? <Minus size={12} /> : <TrendingDown size={12} />}
                      <span className="text-xs font-bold font-mono-nums">
                        {pct > 0 ? '+' : ''}{pct}%
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground/60">{better ? 'better' : 'worse'}</span>
                  </div>

                  {/* Right */}
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{label}</span>
                      <span className={`text-sm font-bold font-mono-nums ${better ? 'text-positive' : 'text-danger'}`}>
                        {formatVal(key, rightVal)}<span className="text-xs text-muted-foreground ml-0.5">{unit}</span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${better ? 'bg-positive' : 'bg-danger'}`}
                        style={{ width: `${(rightVal / maxVal) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Insight */}
          <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <p className="text-xs text-primary/80 leading-relaxed">
              <span className="font-semibold text-primary">Key Insight: </span>
              {activeScenario.id === 'sync-vs-async' && 'Asynchronous processing decouples producer from consumer, enabling 3.7× higher throughput and 87% lower tail latency by eliminating blocking I/O wait times.'}
              {activeScenario.id === 'no-cache-vs-cache' && 'A Redis cache reduces database load by 78% and cuts P99 latency by 94% by serving hot data from memory instead of hitting disk-backed storage.'}
              {activeScenario.id === 'single-vs-replicated' && 'Read replicas distribute 72% of queries away from the primary, dropping DB load from 91% to 28% and improving availability through redundancy.'}
              {activeScenario.id === 'single-vs-lb' && 'Load balancing across 3 servers provides 5.3× throughput gain and improves availability from 97.8% to 99.94% by eliminating the single point of failure.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}